/**
 * THE SHOP'S LIVE CAMPAIGNS, FOR A SURFACE THAT WAS NOT HANDED THEM.
 *
 * Pages that already hold the salon payload pass `salonInfo.campaigns` down to
 * their cards. The cart drawer is not a page: it is mounted once for the whole
 * storefront and knows only the slug in the URL, yet it has to price its lines
 * with the same rule the cards use. So it asks — once per shop, shared by
 * every caller, and remembered for a minute — through the same
 * `GET /config/salon-by-slug/:slug` the pages read (never cached server-side:
 * campaigns are per-date, see config.controller.ts).
 *
 * `null` until the answer arrives; `[]` when it failed, which prices every line
 * at its list price — the drawer's behaviour before campaigns existed, never a
 * wrong discount — while the server quote still carries the real total.
 */
import { useEffect, useState } from 'react';
import { getSalonBySlug } from '../api/storeApi';
import {
  SHOP_CAMPAIGNS_FRESH_MS,
  clearShopCampaigns,
  rememberShopCampaigns,
  setShopCampaignsEntry,
  shopCampaignsEntry,
} from '../utils/shopCampaignsCache';

/** For tests: forget every shop's campaigns. */
export function resetShopCampaignsCache() {
  clearShopCampaigns();
}

export function loadShopCampaigns(slug) {
  if (!slug || typeof getSalonBySlug !== 'function') return Promise.resolve([]);
  const hit = shopCampaignsEntry(slug);
  if (hit?.promise) return hit.promise;
  if (hit && Date.now() - hit.at < SHOP_CAMPAIGNS_FRESH_MS) return Promise.resolve(hit.campaigns);
  const promise = Promise.resolve()
    .then(() => getSalonBySlug(slug))
    .then((data) => {
      const salon = data?.salon ?? data;
      const campaigns = Array.isArray(salon?.campaigns) ? salon.campaigns : [];
      rememberShopCampaigns(slug, campaigns);
      return campaigns;
    })
    .catch(() => {
      setShopCampaignsEntry(slug, null);
      return [];
    });
  setShopCampaignsEntry(slug, { promise });
  return promise;
}

export default function useShopCampaigns(slug, { active = true } = {}) {
  const [state, setState] = useState(() => {
    const hit = shopCampaignsEntry(slug);
    return { slug, campaigns: hit && !hit.promise ? hit.campaigns : null };
  });

  useEffect(() => {
    if (!active || !slug) return undefined;
    let dead = false;
    loadShopCampaigns(slug).then((campaigns) => {
      if (!dead) setState({ slug, campaigns });
    });
    return () => { dead = true; };
  }, [slug, active]);

  return state.slug === slug ? state.campaigns : null;
}
